import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  register: (userData) => api.post('/auth/register', userData),
};

// User APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  changePassword: (userId, oldPassword, newPassword) => 
    api.put(`/users/${userId}/change-password`, null, { params: { old_password: oldPassword, new_password: newPassword } }),
  changeOwnPassword: (oldPassword, newPassword) => 
    api.put('/auth/change-password', null, { params: { old_password: oldPassword, new_password: newPassword } }),
};

// Service APIs
export const serviceAPI = {
  getAll: () => api.get('/services'),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
};

// Task Type APIs
export const taskTypeAPI = {
  getAll: (serviceId = null) => api.get('/task-types', { params: { service_id: serviceId } }),
  create: (data) => api.post('/task-types', data),
  update: (id, data) => api.put(`/task-types/${id}`, data),
  getSubtasks: (taskTypeId) => api.get(`/task-types/${taskTypeId}/subtasks`),
  addSubtask: (taskTypeId, title) => api.post(`/task-types/${taskTypeId}/subtasks`, null, { params: { title } }),
  updateSubtask: (taskTypeId, subtaskId, title) => api.put(`/task-types/${taskTypeId}/subtasks/${subtaskId}`, null, { params: { title } }),
  deleteSubtask: (taskTypeId, subtaskId) => api.delete(`/task-types/${taskTypeId}/subtasks/${subtaskId}`),
};

// Client APIs
export const clientAPI = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
};

// Task APIs
export const taskAPI = {
  getAll: (params = {}) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  updateStatus: (id, status) => api.put(`/tasks/${id}/status`, null, { params: { status } }),
  toggleSubtask: (taskId, subtaskId) => api.put(`/tasks/${taskId}/subtasks/${subtaskId}/toggle`),
  addInvoice: (id, data) => api.put(`/tasks/${id}/invoice`, data),
  editInvoice: (id, data) => api.put(`/tasks/${id}/invoice/edit`, data),
  regeneratePaymentLink: (id) => api.post(`/tasks/${id}/regenerate-payment-link`),
  addReceipt: (id, data) => api.put(`/tasks/${id}/receipt`, data),
  addDiscount: (id, data) => api.put(`/tasks/${id}/discount`, data),
  addSubtask: (taskId, title) => api.post(`/tasks/${taskId}/add-subtask`, null, { params: { title } }),
  startWork: (taskId) => api.post(`/tasks/${taskId}/start-work`),
  stopWork: (taskId, notes = null) => api.post(`/tasks/${taskId}/stop-work`, null, { params: { notes } }),
  forceStopWork: (taskId) => api.post(`/tasks/${taskId}/force-stop-work`),
  getActiveWork: () => api.get('/tasks/active-work'),
  getNextInvoiceNumber: () => api.get('/invoices/next-number'),
  markNonBillable: (id, reason) => api.put(`/tasks/${id}/mark-non-billable`, null, { params: { reason } }),
  markBillable: (id) => api.put(`/tasks/${id}/mark-billable`),
  addReimbursement: (id, data) => api.post(`/tasks/${id}/reimbursement`, data),
  collectReimbursement: (taskId, reimbursementId) => api.put(`/tasks/${taskId}/reimbursement/${reimbursementId}/collect`),
};

// Dashboard APIs
export const dashboardAPI = {
  getMetrics: () => api.get('/dashboard/metrics'),
};

// Admin APIs
export const adminAPI = {
  importData: () => api.post('/admin/import-data'),
};

// Reports APIs
export const reportsAPI = {
  getAuditTrail: (params = {}) => api.get('/reports/audit-trail', { params }),
  getClientWorkReport: (clientId) => api.get(`/reports/client-work/${clientId}`),
};

// Recurring Tasks APIs
export const recurringTaskAPI = {
  getAll: (params = {}) => api.get('/recurring-tasks', { params }),
  getById: (id) => api.get(`/recurring-tasks/${id}`),
  create: (data) => api.post('/recurring-tasks', data),
  update: (id, data) => api.put(`/recurring-tasks/${id}`, data),
  pause: (id) => api.put(`/recurring-tasks/${id}/pause`),
  stop: (id) => api.put(`/recurring-tasks/${id}/stop`),
  remove: (id) => api.delete(`/recurring-tasks/${id}`),
  generate: () => api.post('/recurring-tasks/generate'),
};

// Audit Module APIs
export const auditAPI = {
  // Constants
  getConstants: () => api.get('/audit/constants'),
  // Dashboard
  getDashboardStats: () => api.get('/audit/dashboard/stats'),
  // Engagements
  getEngagements: (params = {}) => api.get('/audit/engagements', { params }),
  getEngagement: (id) => api.get(`/audit/engagements/${id}`),
  createEngagement: (data) => api.post('/audit/engagements', data),
  updateEngagement: (id, data) => api.put(`/audit/engagements/${id}`, data),
  deleteEngagement: (id) => api.delete(`/audit/engagements/${id}`),
  importObservations: (engagementId, sourceAuditId, importMode = 'all') =>
    api.post(`/audit/engagements/${engagementId}/import-observations`, null, { params: { source_audit_id: sourceAuditId, import_mode: importMode } }),
  // Observations
  getObservations: (params = {}) => api.get('/audit/observations', { params }),
  getObservation: (id) => api.get(`/audit/observations/${id}`),
  createObservation: (data) => api.post('/audit/observations', data),
  updateObservation: (id, data) => api.put(`/audit/observations/${id}`, data),
  generateObservation: (id) => api.post(`/audit/observations/${id}/generate`),
  sendForReview: (id) => api.post(`/audit/observations/${id}/send-for-review`),
  approveObservation: (id, remarks = '') => api.post(`/audit/observations/${id}/approve`, null, { params: { remarks } }),
  rejectObservation: (id, remarks = '') => api.post(`/audit/observations/${id}/reject`, null, { params: { remarks } }),
  addManagementResponse: (id, data) => api.post(`/audit/observations/${id}/management-response`, data),
  addRejoinder: (id, data) => api.post(`/audit/observations/${id}/rejoinder`, data),
  // Checklists
  getChecklists: (auditId) => api.get('/audit/checklists', { params: { audit_id: auditId } }),
  createChecklist: (data) => api.post('/audit/checklists', data),
  updateChecklistItem: (checklistId, itemId, data) => api.put(`/audit/checklists/${checklistId}/items/${itemId}`, data),
  // Follow-ups
  getFollowUps: (params = {}) => api.get('/audit/follow-ups', { params }),
  createFollowUp: (data) => api.post('/audit/follow-ups', data),
  updateFollowUp: (id, data) => api.put(`/audit/follow-ups/${id}`, data),
  // Templates
  getTemplates: (params = {}) => api.get('/audit/templates', { params }),
  createTemplate: (data) => api.post('/audit/templates', data),
  deleteTemplate: (id) => api.delete(`/audit/templates/${id}`),
  // Report
  getReportData: (engagementId) => api.get(`/audit/engagements/${engagementId}/report-data`),
  generateSummary: (engagementId) => api.post(`/audit/engagements/${engagementId}/generate-summary`),
};

export default api;
